import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Save, Globe, Shield, Users } from "lucide-react";

interface GeneralSettingsData {
  app_name: string;
  app_description: string;
  support_email: string;
  privacy_policy_url: string;
  terms_of_service_url: string;
  maintenance_mode: boolean;
  user_registration_enabled: boolean;
  max_file_upload_size: number;
}

const GeneralSettings = () => {
  const [settings, setSettings] = useState<GeneralSettingsData>({
    app_name: "NationCard",
    app_description: "Democratic polling platform",
    support_email: "support@nationcard.org",
    privacy_policy_url: "/legal/privacy",
    terms_of_service_url: "legal/terms-of-service",
    maintenance_mode: false,
    user_registration_enabled: true,
    max_file_upload_size: 10,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", [
          "app_name",
          "app_description",
          "support_email",
          "privacy_policy_url",
          "terms_of_service_url",
          "maintenance_mode",
          "user_registration_enabled",
          "max_file_upload_size",
        ]);

      if (error) throw error;

      const settingsMap =
        data?.reduce(
          (acc, item) => {
            acc[item.key] = item.value;
            return acc;
          },
          {} as Record<string, any>
        ) || {};

      setSettings({
        app_name: settingsMap.app_name || "NationCard™",
        app_description:
          settingsMap.app_description || "Democratic Polling Platform",
        support_email: settingsMap.support_email || "support@nationcard.org",
        privacy_policy_url: settingsMap.privacy_policy_url || "/legal/privacy",
        terms_of_service_url: settingsMap.terms_of_service_url || "/terms",
        maintenance_mode: settingsMap.maintenance_mode || false,
        user_registration_enabled:
          settingsMap.user_registration_enabled ?? true,
        max_file_upload_size: settingsMap.max_file_upload_size || 10,
      });
    } catch (error) {
      console.error("Error fetching general settings:", error);
      toast.error("Failed to load general settings");
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const settingsToSave = [
        { key: "app_name", value: settings.app_name },
        { key: "app_description", value: settings.app_description },
        { key: "support_email", value: settings.support_email },
        { key: "privacy_policy_url", value: settings.privacy_policy_url },
        { key: "terms_of_service_url", value: settings.terms_of_service_url },
        { key: "maintenance_mode", value: settings.maintenance_mode },
        {
          key: "user_registration_enabled",
          value: settings.user_registration_enabled,
        },
        { key: "max_file_upload_size", value: settings.max_file_upload_size },
      ];

      for (const setting of settingsToSave) {
        const { error } = await supabase
          .from("app_settings")
          .upsert(setting, { onConflict: "key" });

        if (error) throw error;
      }

      toast.success("General settings saved successfully");
    } catch (error) {
      console.error("Error saving general settings:", error);
      toast.error("Failed to save general settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-nation-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">
          General Settings
        </h2>
        <p className="text-muted-foreground">
          Configure basic application settings and preferences.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Globe className="h-5 w-5" />
            Application Details
          </CardTitle>
          <CardDescription>
            Basic information about the application.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label
              htmlFor="app-name"
              className="text-gray-900 dark:text-gray-100"
            >
              Application Name
            </Label>
            <Input
              id="app-name"
              value={settings.app_name}
              onChange={(e) =>
                setSettings({ ...settings, app_name: e.target.value })
              }
              placeholder="NationCard"
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="app-description"
              className="text-gray-900 dark:text-gray-100"
            >
              Description
            </Label>
            <Input
              id="app-description"
              value={settings.app_description}
              onChange={(e) =>
                setSettings({ ...settings, app_description: e.target.value })
              }
              placeholder="Democratic Polling Platform"
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Shield className="h-5 w-5" />
            Contact & Legal
          </CardTitle>
          <CardDescription>
            Contact and legal information for the application.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label
              htmlFor="support-email"
              className="text-gray-900 dark:text-gray-100"
            >
              Support Email
            </Label>
            <Input
              id="support-email"
              type="email"
              value={settings.support_email}
              onChange={(e) =>
                setSettings({ ...settings, support_email: e.target.value })
              }
              placeholder="support@nationcard.org"
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="privacy-policy-url"
              className="text-gray-900 dark:text-gray-100"
            >
              Privacy Policy URL
            </Label>
            <Input
              id="privacy-policy-url"
              type="url"
              value={settings.privacy_policy_url}
              onChange={(e) =>
                setSettings({ ...settings, privacy_policy_url: e.target.value })
              }
              placeholder="/legal/privacy"
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="terms-of-service-url"
              className="text-gray-900 dark:text-gray-100"
            >
              Terms of Service URL
            </Label>
            <Input
              id="terms-of-service-url"
              type="url"
              value={settings.terms_of_service_url}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  terms_of_service_url: e.target.value,
                })
              }
              placeholder="/terms"
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Users className="h-5 w-5" />
            User Management
          </CardTitle>
          <CardDescription>
            Control user registration and application availability.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label
                htmlFor="maintenance-mode"
                className="text-gray-900 dark:text-gray-100"
              >
                Maintenance Mode
              </Label>
              <p className="text-sm text-muted-foreground">
                Disable public access to the application
              </p>
            </div>
            <Switch
              id="maintenance-mode"
              checked={settings.maintenance_mode}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, maintenance_mode: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label
                htmlFor="user-registration"
                className="text-gray-900 dark:text-gray-100"
              >
                User Registration
              </Label>
              <p className="text-sm text-muted-foreground">
                Enable or disable new user registrations
              </p>
            </div>
            <Switch
              id="user-registration"
              checked={settings.user_registration_enabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, user_registration_enabled: checked })
              }
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="max-file-size"
              className="text-gray-900 dark:text-gray-100"
            >
              Max File Upload Size (MB)
            </Label>
            <Input
              id="max-file-size"
              type="number"
              min="1"
              max="100"
              value={settings.max_file_upload_size}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  max_file_upload_size: parseInt(e.target.value) || 10,
                })
              }
              className="w-32 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
            <p className="text-sm text-muted-foreground">
              Maximum file upload size for documents and media
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="pt-4">
        <Button
          onClick={saveSettings}
          disabled={isSaving}
          className="w-full sm:w-auto bg-nation-blue hover:bg-nation-blue/90 text-white border-0"
        >
          {isSaving ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" /> Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default GeneralSettings;
